import { withHTMLComponent } from '@updater-local/custom-element-v1';
import '@updater-components/cta';
import '@updater-components/loading-spinner';
import html from './index.html';
import styles from './index.css';

const CLASS_NAMES = {
  loaded: 'upd-loaded',
  loading: 'upd-loading',
  error: 'upd-loading-error',
};

// Safari detection: https://stackoverflow.com/questions/5899783/detect-safari-using-jquery
const isSafari = !!navigator.userAgent.match(/Version\/[\d\.]+.*Safari/);

export default class Loading extends withHTMLComponent(styles, html)() {
  static props = ['emptyValue', 'value', 'renderProp'];

  static observedAttributes = ['loading', 'error'];

  static reflectedAttributes = [{ name: 'overlay', boolean: true }];

  constructor() {
    super();

    this._defaultSlot = this.shadowRoot.querySelector(
      'slot:not([name$="-message"])'
    );
    this._dismiss = this.shadowRoot.querySelector('#dismiss');
  }

  connected() {
    // The "dismiss" button may not exist because it's default slotted content.
    if (this._dismiss) {
      this._dismiss.addEventListener('click', this._dismissHandler);
    }
  }

  disconnected() {
    if (this._dismiss) {
      this._dismiss.removeEventListener('click', this._dismissHandler);
    }
  }

  applyClassesFromAttribues(loading, error) {
    this.classList.remove(CLASS_NAMES.error);
    this.classList.remove(CLASS_NAMES.loaded);
    this.classList.add(CLASS_NAMES.loading);
    // "Override" promise handling behavior
    const addLoadingClass = loading === 'true' || loading === true;
    const addErrorClass =
      error === 'true' ||
      (typeof error === 'string' && error && error !== 'false') ||
      error === true;

    const updateLoadingClass =
      addLoadingClass || addErrorClass ? 'add' : 'remove';
    const updateErrorClass = addErrorClass ? 'add' : 'remove';

    this.classList.remove(CLASS_NAMES.loaded);
    this.classList[updateErrorClass](CLASS_NAMES.error);
    this.classList[updateLoadingClass](CLASS_NAMES.loading);
    if (!addLoadingClass && !addErrorClass) {
      this.classList.add(CLASS_NAMES.loaded);
    }
  }

  updated({ value, loading, error }) {
    // TODO: Should this be checking for !value instead?
    if (loading !== undefined || error !== undefined) {
      this.applyClassesFromAttribues(loading, error);
      return;
    }
    this.classList.remove(CLASS_NAMES.error);
    this.classList.remove(CLASS_NAMES.loaded);
    this.classList.add(CLASS_NAMES.loading);

    // Allow "clearing" values from the wrapped content using `emptyValue`.
    if (this.emptyValue !== undefined) {
      this._passValueToSlottedElements(this.emptyValue);
    }

    value.then(
      result => {
        // Ignore `result` if `value` changed, there's a new Promise resolving.
        if (value === this.value) {
          // Allow "opting out" of value assignment by resolving `undefined`.
          if (result !== undefined) {
            this._passValueToSlottedElements(result);
          }

          this.classList.remove(CLASS_NAMES.loading);
          this.classList.add(CLASS_NAMES.loaded);

          if (isSafari) {
            this._forceSafari10Reflow();
          }
        }
      },
      // TODO: Consider adding logic to pass `error` as `value` to any slotted
      // non-text elements, allowing a way to display different error messages
      // based on the `error`.
      err => {
        this.classList.add(CLASS_NAMES.error);
        throw err;
      }
    );
  }

  // Pass the resolved value to all slotted elements' `value` prop.
  // TODO: More fine-grained control over how the result is passed.
  // Perhaps a key-value map between element selectors and values.
  _passValueToSlottedElements = value => {
    Array.from(this._defaultSlot.assignedNodes())
      .filter(({ nodeType }) => nodeType === Node.ELEMENT_NODE)
      .forEach(element => (element.value = value));
  };

  _dismissHandler = () => {
    this.classList.remove(CLASS_NAMES.loading, CLASS_NAMES.error);
    this.dispatchEvent(
      new CustomEvent('dismiss', {
        bubbles: true,
        composed: true,
      })
    );
  };

  _forceSafari10Reflow = () => {
    // HACK ALERT! Safari 10.x doesn't like the repaint trick with upd-loaded
    // The simple repaint trick also breaks offers page (company profile modal) in production, so its a no-go
    // This will detect if we are in Safari & apply a force reflow fix
    // Reflow hack: https://stackoverflow.com/questions/3485365/how-can-i-force-webkit-to-redraw-repaint-to-propagate-style-changes
    console.log('Applying safari reflow hack'); // keep this so we know when this runs
    this.style.display = 'none';
    this.offsetHeight;
    this.style.display = '';
  };
}

customElements.define('upd-moveiq-loading', Loading);
